import { FC } from 'react'
import { RetroWindow, RetroButton, PixelText } from '@/components/retro'
import { useGame } from '@/providers'

export const MessagePanel: FC = () => {
  const { welcomeMessage, timeSkipMessage, lastTradeResult, marketEvents, clearMessages } = useGame()

  const hasMessage = welcomeMessage || timeSkipMessage || lastTradeResult

  if (!hasMessage && marketEvents.length === 0) {
    return (
      <RetroWindow title="TERMINAL" className="h-full">
        <div className="h-full flex items-center justify-center">
          <PixelText size="xs" color="muted">
            Waiting for action...
          </PixelText>
        </div>
      </RetroWindow>
    )
  }

  return (
    <RetroWindow title="TERMINAL" className="h-full">
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto space-y-3 p-1">
          {/* Welcome Message */}
          {welcomeMessage && (
            <div className="p-3 border-2 border-game-accent bg-game-accent/10">
              <pre className="font-retro text-sm text-game-text whitespace-pre-wrap">
                {welcomeMessage}
              </pre>
            </div>
          )}

          {/* Time Skip Message */}
          {timeSkipMessage && (
            <div className="p-3 border-2 border-game-gold bg-game-gold/10">
              <PixelText size="xs" color="gold" className="mb-2">TIME PASSED</PixelText>
              <pre className="font-retro text-sm text-game-text whitespace-pre-wrap">
                {timeSkipMessage}
              </pre>
            </div>
          )}

          {/* Last Trade Result */}
          {lastTradeResult && (
            <div className={`p-3 border-2 ${
              lastTradeResult.success
                ? 'border-game-profit bg-game-profit/10'
                : 'border-game-loss bg-game-loss/10'
            }`}>
              <PixelText
                size="xs"
                color={lastTradeResult.success ? 'profit' : 'loss'}
                className="mb-1"
              >
                {lastTradeResult.success ? 'TRADE EXECUTED' : 'TRADE FAILED'}
              </PixelText>
              <p className="font-retro text-sm text-game-text">
                {lastTradeResult.message}
              </p>
              <p className="font-retro text-xs text-game-muted mt-2 italic">
                "{lastTradeResult.degenQuote}"
              </p>
            </div>
          )}

          {/* Market Events */}
          {marketEvents.length > 0 && (
            <div className="space-y-2">
              <PixelText size="xs" color="muted">RECENT MARKET EVENTS</PixelText>
              {marketEvents.slice(0, 5).map((event, i) => (
                <div
                  key={i}
                  className={`p-2 border ${
                    event.type === 'pump' || event.type === 'shill'
                      ? 'border-game-profit/50 bg-game-profit/5'
                      : event.type === 'dump' || event.type === 'fud' || event.type === 'rug'
                        ? 'border-game-loss/50 bg-game-loss/5'
                        : 'border-game-border bg-game-panel/50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`font-pixel text-xs ${
                      event.type === 'pump' ? 'text-game-profit' :
                      event.type === 'dump' ? 'text-game-loss' :
                      event.type === 'rug' ? 'text-game-loss' :
                      event.type === 'shill' ? 'text-game-gold' :
                      event.type === 'fud' ? 'text-game-loss' :
                      'text-game-accent'
                    }`}>
                      {event.type.toUpperCase()}
                    </span>
                    {event.token && (
                      <span className="font-pixel text-xs text-game-accent">{event.token}</span>
                    )}
                  </div>
                  <p className="font-retro text-xs text-game-text mt-1">{event.headline}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Clear Button */}
        {hasMessage && (
          <div className="pt-2 border-t border-game-border">
            <RetroButton
              variant="default"
              size="sm"
              className="w-full"
              onClick={clearMessages}
            >
              CLEAR
            </RetroButton>
          </div>
        )}
      </div>
    </RetroWindow>
  )
}
